import { resolveProviderConfigs } from "../providers/resolve.js";
import type { ProviderRuntimeScope } from "../providers/runtime-filter.js";
import {
  assertHttpUrl,
  assertNonEmptyString,
  assertOptionalString,
  parseProviderRuntime,
  schemaError,
} from "./validate.js";

/** Default fallback when no providers are configured */
export const DEFAULT_CONSOLE_PROVIDER: ConsoleProviderConfig = {
  type: "console",
  level: "error",
  runtime: "both",
};

export interface ConsoleProviderConfig {
  type: "console";
  id?: string;
  level: "debug" | "info" | "warn" | "error";
  runtime: ProviderRuntimeScope;
}

export interface HttpProviderConfig {
  type: "http";
  id?: string;
  endpoint: string;
  method: "POST" | "PUT";
  headers?: Record<string, string>;
  timeoutMs?: number;
  runtime: ProviderRuntimeScope;
  batch?: {
    maxSize: number;
    flushIntervalMs: number;
  };
}

export interface CallbackProviderConfig {
  type: "callback";
  id: string;
  runtime: ProviderRuntimeScope;
}

export type CoreProviderConfig =
  | ConsoleProviderConfig
  | HttpProviderConfig
  | CallbackProviderConfig;

const CONSOLE_LEVELS = new Set(["debug", "info", "warn", "error"]);
const HTTP_METHODS = new Set(["POST", "PUT"]);

function parseHeaders(value: unknown): Record<string, string> | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw schemaError("headers must be an object");
  }
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val !== "string") {
      throw schemaError("headers values must be strings");
    }
    out[key] = val;
  }
  return out;
}

function parseBatch(value: unknown): HttpProviderConfig["batch"] | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw schemaError("batch must be an object");
  }
  const raw = value as Record<string, unknown>;
  const maxSize = raw.maxSize;
  const flushIntervalMs = raw.flushIntervalMs;
  if (typeof maxSize !== "number" || maxSize <= 0 || !Number.isInteger(maxSize)) {
    throw schemaError("batch.maxSize must be a positive integer");
  }
  if (
    typeof flushIntervalMs !== "number" ||
    flushIntervalMs <= 0 ||
    !Number.isInteger(flushIntervalMs)
  ) {
    throw schemaError("batch.flushIntervalMs must be a positive integer");
  }
  return { maxSize, flushIntervalMs };
}

export function parseCoreProviderConfig(input: unknown): CoreProviderConfig {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    throw schemaError("provider must be an object");
  }

  const raw = input as Record<string, unknown>;
  const type = raw.type;

  if (type === "console") {
    const level = raw.level ?? "error";
    if (!CONSOLE_LEVELS.has(level as string)) {
      throw schemaError('console level must be "debug", "info", "warn", or "error"');
    }
    return {
      type: "console",
      id: assertOptionalString(raw.id, "id"),
      level: level as ConsoleProviderConfig["level"],
      runtime: parseProviderRuntime(raw.runtime),
    };
  }

  if (type === "http") {
    const method = raw.method ?? "POST";
    if (!HTTP_METHODS.has(method as string)) {
      throw schemaError('http method must be "POST" or "PUT"');
    }
    const timeoutMs = raw.timeoutMs;
    if (
      timeoutMs !== undefined &&
      (typeof timeoutMs !== "number" || timeoutMs <= 0 || !Number.isInteger(timeoutMs))
    ) {
      throw schemaError("timeoutMs must be a positive integer");
    }
    return {
      type: "http",
      id: assertOptionalString(raw.id, "id"),
      endpoint: assertHttpUrl(raw.endpoint, "endpoint"),
      method: method as HttpProviderConfig["method"],
      headers: parseHeaders(raw.headers),
      timeoutMs,
      runtime: parseProviderRuntime(raw.runtime),
      batch: parseBatch(raw.batch),
    };
  }

  if (type === "callback") {
    return {
      type: "callback",
      id: assertNonEmptyString(raw.id, "id"),
      runtime: parseProviderRuntime(raw.runtime),
    };
  }

  throw schemaError(`unknown provider type: ${String(type)}`);
}

export function parseCoreProviderConfigs(input: unknown): CoreProviderConfig[] {
  if (input === undefined) {
    return resolveProviderConfigs(undefined) as CoreProviderConfig[];
  }
  if (!Array.isArray(input)) {
    throw schemaError("providers must be an array");
  }
  return resolveProviderConfigs(
    input.map(parseCoreProviderConfig),
  ) as CoreProviderConfig[];
}
